import { createElement } from "react";
import { createRoute, lazyRouteComponent } from "@tanstack/react-router";
import { requirePermission } from "../../../../shared/routing/requirePermission";
import {
  SUBSCRIPTIONS_CREATE,
  SUBSCRIPTIONS_UPDATE,
  SUBSCRIPTIONS_VIEW,
} from "../../subscriptionsPermissions";
import { subscriptionsSearchSchema } from "./searchSchemas";
import type { SubscriptionRouteFactory } from "./subscriptionRouteContext";

const SubscriptionDetailScreen = lazyRouteComponent(
  () => import("../SubscriptionDetailScreen"),
  "SubscriptionDetailScreen",
);
const SubscriptionFormScreen = lazyRouteComponent(
  () => import("../SubscriptionFormScreen"),
  "SubscriptionFormScreen",
);
const SubscriptionsListScreen = lazyRouteComponent(
  () => import("../SubscriptionsListScreen"),
  "SubscriptionsListScreen",
);

export const createSubscriptionRoutes: SubscriptionRouteFactory = (deps) => {
  const editRoute = createRoute({
    getParentRoute: deps.context.getParentRoute,
    path: `${deps.context.basePath}/$id/edit`,
    beforeLoad: requirePermission([SUBSCRIPTIONS_UPDATE]),
    component: function Edit() {
      // Route-scoped params, not a manual pathname split: during the
      // navigate-away transition after a successful update, the global
      // location already points at the new URL while this route's own match
      // (and its params) only change once this component actually unmounts,
      // so it never briefly re-renders with the wrong id.
      const { id } = editRoute.useParams() as unknown as { id: string };
      return createElement(SubscriptionFormScreen, {
        repository: deps.subscriptions,
        basePath: deps.context.basePath,
        subscriptionId: id,
        companies: deps.companies,
        catalog: deps.catalog,
      });
    },
  });

  const detailRoute = createRoute({
    getParentRoute: deps.context.getParentRoute,
    path: `${deps.context.basePath}/$id`,
    beforeLoad: requirePermission([SUBSCRIPTIONS_VIEW]),
    component: function Detail() {
      const { id } = detailRoute.useParams() as unknown as { id: string };
      return createElement(SubscriptionDetailScreen, {
        repository: deps.subscriptions,
        basePath: deps.context.basePath,
        subscriptionId: id,
      });
    },
  });

  return [
    createRoute({
      getParentRoute: deps.context.getParentRoute,
      path: deps.context.basePath,
      validateSearch: subscriptionsSearchSchema,
      beforeLoad: requirePermission([SUBSCRIPTIONS_VIEW]),
      component: () =>
        createElement(SubscriptionsListScreen, {
          repository: deps.subscriptions,
          companiesRepository: deps.companies,
          basePath: deps.context.basePath,
        }),
    }),
    createRoute({
      getParentRoute: deps.context.getParentRoute,
      path: `${deps.context.basePath}/create`,
      beforeLoad: requirePermission([SUBSCRIPTIONS_CREATE]),
      component: () =>
        createElement(SubscriptionFormScreen, {
          repository: deps.subscriptions,
          basePath: deps.context.basePath,
          companies: deps.companies,
          catalog: deps.catalog,
        }),
    }),
    editRoute,
    detailRoute,
  ];
};
